import { useMutation } from '@src/query';


/**
 * Settings → Channels mutation hooks (T10). Each wraps the shared `useMutation`
 * engine with the injected-`queryClient` `mutateFn({ apiClient, queryClient })`
 * contract — the corelib query singleton is NOT imported here. Invalidation runs
 * inside `mutateFn` (the one place `queryClient` is injected). The caller passes
 * the channels-list `queryKey` it already holds from `useChannelsQuery`, so the
 * exact cache key is preserved unchanged.
 *
 * Mirrors `query/garage/useDonorMutations.js`: one
 * `query/<area>/use<Thing>Mutations.js` module re-exported through the
 * `@src/query` barrel.
 */

/** Enable/disable a channel. Variables: `{ sellerId, channelId, enabled, queryKey }`. */
function useChannelToggleMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, sellerId, channelId, enabled, queryKey }) => apiClient
      .request({ url: `/sellers/${sellerId}/channels/${channelId}/`, method: 'PATCH', payload: { enabled } })
      .then((data) => queryClient.invalidateQueries({ queryKey }).then(() => data)),
    ...params,
  });
}


/** Reorder channels. Variables: `{ sellerId, ids, queryKey }`. */
function useChannelReorderMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, sellerId, ids, queryKey }) => apiClient
      .request({ url: `/sellers/${sellerId}/channels/reorder/`, method: 'POST', payload: { ids } })
      .then((data) => queryClient.invalidateQueries({ queryKey }).then(() => data)),
    ...params,
  });
}


/** Create/update a custom channel. Variables: `{ sellerId, channelId, payload,
 * queryKey }`. PATCHes when `channelId`, otherwise POSTs. */
function useChannelSaveMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, sellerId, channelId, payload, queryKey }) => apiClient
      .request({
        url: channelId
          ? `/sellers/${sellerId}/channels/${channelId}/`
          : `/sellers/${sellerId}/channels/`,
        method: channelId ? 'PATCH' : 'POST',
        payload,
      })
      .then((data) => queryClient.invalidateQueries({ queryKey }).then(() => data)),
    ...params,
  });
}


/** Delete a custom channel. Variables: `{ sellerId, channelId, queryKey }`. */
function useChannelDeleteMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, sellerId, channelId, queryKey }) => apiClient
      .request({ url: `/sellers/${sellerId}/channels/${channelId}/`, method: 'DELETE' })
      .then((data) => queryClient.invalidateQueries({ queryKey }).then(() => data)),
    ...params,
  });
}


export {
  useChannelDeleteMutation,
  useChannelReorderMutation,
  useChannelSaveMutation,
  useChannelToggleMutation,
};
